import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useMatch, useRouter, useRouterState } from '@tanstack/react-router';

type SetUrlHash = (hash: string, options?: { replace?: boolean }) => void;

// The hash without '#'. Back, Forward, a typed hash and a Link all change it,
// so derive view state from it in an effect instead of reading it once on mount.
// While the router moves to another path, this keeps the last hash of the path
// the caller rendered on, so a leaving page never reacts to the next page's
// hash. A setter belongs to the render that made it, so a write that lands
// after leaving, or after the page moved to other params, is dropped.
export const useUrlHash = (): [string, SetUrlHash] => {
  const router = useRouter();
  const matchId = useMatch({ strict: false, select: (m) => m.id });
  const [own, setOwn] = useState(() => ({
    matchId,
    pathname: router.state.location.pathname,
  }));
  if (own.matchId !== matchId)
    setOwn({ matchId, pathname: router.state.location.pathname });

  const routedHash = useRouterState({
    select: (s) =>
      s.location.pathname === own.pathname ? s.location.hash : null,
  });
  const [hash, setOwnHash] = useState<string>(routedHash ?? '');
  if (routedHash !== null && routedHash !== hash) setOwnHash(routedHash);

  const activeOwner = useRef<typeof own | null>(null);
  useLayoutEffect(() => {
    activeOwner.current = own;
    return () => {
      activeOwner.current = null;
    };
  }, [own]);

  const setHash = useCallback<SetUrlHash>(
    (next, { replace = false } = {}) => {
      if (activeOwner.current !== own) return;
      if (router.state.location.pathname !== own.pathname) return;
      void router.navigate({
        to: '.',
        search: true,
        hash: next,
        replace,
        resetScroll: false,
        hashScrollIntoView: false,
        ignoreBlocker: true,
      });
      router.history.flush();
    },
    [router, own],
  );

  return [hash, setHash];
};
